import {
  getGroupedVouchers,
  getVoucherGroupByType,
  claimGroupedVouchersByType,
  claimVoucher,
  getUserVouchers,
} from '../assets/middleware-api.js';
import {
  createVoucherCardElement,
  bindClaimHandlers,
  bindViewDetailHandlers,
  markCardClaimed,
} from '../assets/oc_voucher_card.js?v=1.0.3';

const VIEW_MORE_PAGE_SIZE = 20;

const containers = Array.from(document.querySelectorAll('.grouped-voucher-list'));

function setupGroup(groupEl, ctx) {
  const { isCustomerLoggedIn, customerVouchers, initialLimit } = ctx;
  const type = groupEl.dataset.type;
  const listEl = groupEl.querySelector('.oc-voucher-grid-list');
  const claimAllBtn = groupEl.querySelector('.oc-voucher-claim-all');
  const moreBtn = groupEl.querySelector('.oc-voucher-toggle-more');

  const state = {
    type,
    items: [],
    hasMore: false,
    initialLimit,
    expanded: false,
  };

  function renderAll() {
    listEl.innerHTML = '';
    const visibleItems = state.expanded ? state.items : state.items.slice(0, state.initialLimit);
    visibleItems.forEach(voucher => {
      listEl.appendChild(createVoucherCardElement(voucher, customerVouchers));
    });
    bindClaimHandlers(listEl, { isCustomerLoggedIn, claimFn: claimVoucher });
    bindViewDetailHandlers(listEl);
    refreshClaimAllState();
  }

  function refreshClaimAllState() {
    if (!claimAllBtn) return;
    const claimable = listEl.querySelectorAll('.claim').length;
    if (claimable === 0) {
      claimAllBtn.disabled = true;
      claimAllBtn.textContent = 'All Claimed';
    } else {
      claimAllBtn.disabled = false;
      claimAllBtn.textContent = 'Claim All';
    }
  }

  function refreshMoreButton() {
    if (!moreBtn) return;
    const canExpand = state.hasMore || state.items.length > state.initialLimit;
    if (!canExpand) {
      moreBtn.hidden = true;
      return;
    }
    moreBtn.hidden = false;
    moreBtn.textContent = state.expanded ? 'View Less' : 'View More';
  }

  if (moreBtn) {
    moreBtn.addEventListener('click', async () => {
      if (state.expanded) {
        state.expanded = false;
        renderAll();
        refreshMoreButton();
        return;
      }

      if (!state.hasMore) {
        state.expanded = true;
        renderAll();
        refreshMoreButton();
        return;
      }

      moreBtn.disabled = true;
      const originalText = moreBtn.textContent;
      moreBtn.textContent = 'Loading...';
      try {
        const res = await getVoucherGroupByType(type, {
          start: state.items.length,
          limit: VIEW_MORE_PAGE_SIZE,
        });
        const newItems = res.items || [];
        state.items = state.items.concat(newItems);
        const total = res.pagination?.total ?? state.items.length;
        state.hasMore = state.items.length < total;
        state.expanded = true;
        renderAll();
      } catch (err) {
        if (typeof showToast === 'function') {
          showToast('Failed to load more vouchers.', 'error');
        }
        moreBtn.textContent = originalText;
      } finally {
        moreBtn.disabled = false;
        refreshMoreButton();
      }
    });
  }

  if (claimAllBtn) {
    claimAllBtn.addEventListener('click', async () => {
      if (!isCustomerLoggedIn) {
        if (typeof showToast === 'function') {
          showToast('Opps... Please login first before claim the voucher... Redirecting to login page', 'error');
        }
        setTimeout(() => {
          window.location.href = '/account/login';
        }, 2000);
        return;
      }

      claimAllBtn.disabled = true;
      const previousText = claimAllBtn.textContent;
      claimAllBtn.textContent = 'Claiming...';
      try {
        const res = await claimGroupedVouchersByType(type);
        const results = res.results || [];
        results.forEach(r => {
          if (r.status !== 'claimed') return;
          const card = listEl.querySelector(`[data-document-id="${r.documentId}"]`);
          if (card) markCardClaimed(card);
        });

        const summary = res.summary || { claimedCount: 0, skippedCount: 0, failedCount: 0 };
        const { claimedCount = 0, skippedCount = 0, failedCount = 0 } = summary;
        if (typeof showToast === 'function') {
          const msg = `${claimedCount} claimed, ${skippedCount} skipped, ${failedCount} failed`;
          showToast(msg, failedCount > 0 ? 'error' : 'success');
        }
        refreshClaimAllState();
      } catch (err) {
        if (typeof showToast === 'function') {
          showToast('Failed to claim vouchers. Please try again.', 'error');
        }
        claimAllBtn.disabled = false;
        claimAllBtn.textContent = previousText;
      }
    });
  }

  return {
    type,
    state,
    setItems(items, hasMore) {
      state.items = items || [];
      state.hasMore = !!hasMore;
      state.expanded = false;
      if (state.items.length === 0) {
        groupEl.hidden = true;
        return;
      }
      groupEl.hidden = false;
      renderAll();
      refreshMoreButton();
    },
  };
}

async function init(container) {
  const isCustomerLoggedIn = container.dataset.customerLoggedIn === 'true';
  const initialLimit = parseInt(container.dataset.initialLimit, 10) || 6;

  const groupEls = Array.from(container.querySelectorAll('.oc-voucher-group'));
  if (groupEls.length === 0) return;

  groupEls.sort((a, b) => {
    const ao = parseInt(a.dataset.order, 10) || 0;
    const bo = parseInt(b.dataset.order, 10) || 0;
    return ao - bo;
  });
  groupEls.forEach(el => container.appendChild(el));

  let customerVouchers = [];
  try {
    if (isCustomerLoggedIn) {
      const customerRes = await getUserVouchers(0, 100);
      customerVouchers = customerRes.userVouchers || [];
    }
  } catch (err) {
    console.error('Failed to load customer vouchers:', err);
  }

  const ctx = { isCustomerLoggedIn, customerVouchers, initialLimit };
  const groups = groupEls.map(el => ({ el, group: setupGroup(el, ctx) }));

  try {
    const overview = await getGroupedVouchers({ limit: initialLimit });
    if (!overview) {
      groups.forEach(({ el }) => { el.hidden = true; });
      return;
    }
    groups.forEach(({ el, group }) => {
      const bucket = overview[group.type];
      if (!bucket) {
        el.hidden = true;
        return;
      }
      group.setItems(bucket.items, bucket.hasMore);
    });
  } catch (err) {
    console.error('Failed to load grouped vouchers:', err);
    groups.forEach(({ el }) => { el.hidden = true; });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  containers.forEach(init);
});
